import {MouseEvent, useCallback, useEffect, useMemo, useState} from 'react';
import {styled} from 'styled-components';

import {randomStringUnsafe} from '@shared/lib/random_utils';
import {NonEmptyArray} from '@shared/lib/type_utils';
import {NzbsuSearchSort} from '@shared/models';

import {Button} from '@shared-web/components/core/button';
import {Input} from '@shared-web/components/core/input';
import {useStringQueryString} from '@shared-web/lib/use_query_string';

import {NzbTable} from '@src/components/nzb_table';
import {apiCall} from '@src/lib/api';
import {batchSetNzbsu, sortNzbsuIds} from '@src/stores/nzbsu_store';

interface ResState {
  query: string;
  itemIds: string[];
  offset: number;
  total: number;
}

const MOVIE_ALL_CAT_ID = 2000;

const CATS: NonEmptyArray<{id: number; label: string; name: string}> = [
  {id: 2045, label: 'Movies UHD', name: 'movie_uhd'},
  {id: MOVIE_ALL_CAT_ID, label: 'Movies All', name: 'movie_all'},
  {id: 5045, label: 'TV UHD', name: 'tv_uhd'},
  {id: 5000, label: 'TV All', name: 'tv_all'},
];

export const NzbsuPage: React.FC = () => {
  // URL params
  const [searchText, setSearchText, searchTextRef] = useStringQueryString('search', '');
  const [sort, setSort, sortRef] = useStringQueryString<NzbsuSearchSort>(
    'sort',
    NzbsuSearchSort.PostedDesc
  );
  const [_, setCatName, catRef] = useStringQueryString('cat', 'movie_all');
  const getCatId = useCallback(
    () => CATS.find(c => c.name === catRef.current)?.id ?? MOVIE_ALL_CAT_ID,
    [catRef]
  );

  // State
  const [res, setRes] = useState<ResState | undefined>();
  const [loading, setLoading] = useState(false);
  const [refreshId, setRefreshId] = useState(randomStringUnsafe(10));
  const [resortId, setResortId] = useState(randomStringUnsafe(10));
  const refresh = useCallback(() => setRefreshId(randomStringUnsafe(10)), []);
  const resort = useCallback(() => setResortId(randomStringUnsafe(10)), []);

  useEffect(() => {
    const query = searchTextRef.current;
    if (query.length === 0) {
      return;
    }
    setRes(undefined);
    setLoading(true);
    apiCall('POST /search-nzbsu', {query, cat: getCatId(), offset: 0, sort: sortRef.current})
      .then(res => {
        const {items, offset, total} = res;
        batchSetNzbsu(items.map(item => ({key: item.guid, value: item})));
        setRes({itemIds: items.map(item => item.guid), offset, total, query});
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [getCatId, refreshId, searchTextRef, sortRef]);

  const handleSearchClick = useCallback(() => {
    refresh();
  }, [refresh]);

  const handleLoadMoreClick = useCallback(() => {
    if (!res) {
      return;
    }
    setLoading(true);
    apiCall('POST /search-nzbsu', {
      query: res.query,
      cat: getCatId(),
      offset: res.offset + res.itemIds.length,
      sort: sortRef.current,
    })
      .then(res =>
        setRes(current => {
          if (!current) {
            return;
          }
          batchSetNzbsu(res.items.map(item => ({key: item.guid, value: item})));
          return {
            itemIds: [...current.itemIds, ...res.items.map(item => item.guid)],
            offset: res.offset,
            total: current.total,
            query: current.query,
          };
        })
      )
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [getCatId, res, sortRef]);

  const handleSortChange = useCallback(
    (newSort: NzbsuSearchSort) => {
      setSort(newSort);
      // Only refresh if we don't have all the items, otherwise sorting in the frontend is enough
      if (!res || res.itemIds.length < res.total) {
        refresh();
      } else {
        resort();
      }
    },
    [refresh, res, resort, setSort]
  );

  const handleCatClick = useCallback(
    (evt: MouseEvent<HTMLElement>) => {
      const newCat = CATS.find(c => String(c.id) === evt.currentTarget.getAttribute('data-cat'));
      if (!newCat) {
        return;
      }
      setCatName(newCat.name);
      refresh();
    },
    [refresh, setCatName]
  );

  const sortedIds = useMemo(
    () => (res ? sortNzbsuIds(res.itemIds, sortRef.current) : undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [res, resortId]
  );

  return (
    <Wrapper>
      <ButtonLine>
        {CATS.map(el => (
          <ButtonLineItem
            key={el.id}
            data-cat={el.id}
            $selected={el.id === getCatId()}
            onClick={handleCatClick}
          >
            {el.label}
          </ButtonLineItem>
        ))}
      </ButtonLine>

      <Form>
        <Input
          value={searchText}
          syncState={setSearchText}
          autoFocus
          placeholder="Search something"
          width="100%"
          overrides={{paddingLeft: 16, paddingRight: 16, fontSize: 20, fontWeight: 700, height: 57}}
        />
        <Button onClick={handleSearchClick} loading={loading} keyboardSubmit>
          SEARCH
        </Button>
      </Form>

      {sortedIds || loading ? (
        <NzbTable ids={sortedIds} sort={sort} total={res?.total} onSortChange={handleSortChange} />
      ) : (
        <></>
      )}

      {res && sortedIds && sortedIds.length < res.total ? (
        <LoadMoreButton onClick={handleLoadMoreClick} loading={loading}>
          LOAD MORE
        </LoadMoreButton>
      ) : (
        <></>
      )}
    </Wrapper>
  );
};
NzbsuPage.displayName = 'HomePage';

const Wrapper = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Form = styled.div`
  display: flex;
  gap: 16px;
`;

const ButtonLine = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
  flex-wrap: wrap;
`;

const ButtonLineItem = styled.div<{$selected: boolean}>`
  padding: 8px 12px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  color: white;
  background: #ffffff11;
  border-radius: 16px;
  border: solid 2px ${p => (p.$selected ? '#a34c00' : 'transparent')};
  &:hover {
    background: #ffffff22;
  }
`;

const LoadMoreButton = styled(Button)`
  width: 100%;
`;
